/**
 * Workspace Sharing Service — shareable invite links with editor/viewer roles (same pattern as space sharing).
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { Workspace, WorkspaceShare, WorkspaceShareAccess } from "@shared/schema";
import { workspacesService } from "@/services/workspacesService";
import type { WorkspaceShareMemberRole } from "@/services/sharedWorkspacePermissionService";

export interface CreateWorkspaceShareLinkParams {
  workspaceId: string;
  accessType?: "restricted" | "anyone" | "organization";
  expiresAt?: Date | null;
  defaultMemberRole?: "editor" | "viewer";
}

export interface WorkspaceMemberEntry {
  userId: string;
  workspaceRole: WorkspaceShareMemberRole;
  status: "accepted";
  joinedAt: string;
}

/** Map auth user id -> display label (from workspace_owner_member_display_info RPC). */
export type WorkspaceMemberDisplayLabels = Record<string, string>;

export interface WorkspaceShareLinkValidationResult {
  isValid: boolean;
  share: WorkspaceShare | null;
  workspace: Workspace | null;
  access: WorkspaceShareAccess | null;
  message: string;
}

function mapRowToWorkspaceShare(row: Record<string, unknown>): WorkspaceShare {
  return {
    id: String(row.id),
    workspaceId: String(row.workspace_id),
    createdByUserId: String(row.created_by_user_id),
    shareToken: String(row.share_token),
    accessType: String(row.access_type ?? "restricted"),
    expiresAt: row.expires_at ? new Date(String(row.expires_at)) : null,
    isActive: String(row.is_active ?? "true"),
    defaultMemberRole: ((row.default_member_role as string) || "viewer") as "editor" | "viewer",
    createdAt: new Date(String(row.created_at)),
    updatedAt: new Date(String(row.updated_at)),
  } as WorkspaceShare;
}

function mapRowToWorkspaceShareAccess(row: Record<string, unknown>): WorkspaceShareAccess {
  return {
    id: String(row.id),
    shareId: String(row.share_id),
    userId: String(row.user_id),
    status: String(row.status ?? "pending"),
    role: ((row.role as string) || "viewer") as "editor" | "viewer",
    accessedAt: row.accessed_at ? new Date(String(row.accessed_at)) : null,
    respondedAt: row.responded_at ? new Date(String(row.responded_at)) : null,
    createdAt: new Date(String(row.created_at)),
    updatedAt: new Date(String(row.updated_at)),
  } as WorkspaceShareAccess;
}

function mapRowToWorkspace(row: Record<string, unknown>): Workspace {
  return {
    id: String(row.id),
    name: String(row.name),
    slug: row.slug != null ? String(row.slug) : null,
    createdByUserId: String(row.created_by_user_id),
    createdAt: new Date(String(row.created_at)),
    updatedAt: new Date(String(row.updated_at)),
  };
}

function parseRpcValidationPayload(data: unknown): WorkspaceShareLinkValidationResult {
  let payload: unknown = data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return {
        isValid: false,
        share: null,
        workspace: null,
        access: null,
        message: "Invalid response",
      };
    }
  }
  if (!payload || typeof payload !== "object") {
    return {
      isValid: false,
      share: null,
      workspace: null,
      access: null,
      message: "Invalid response",
    };
  }
  const o = payload as Record<string, unknown>;
  const isValid = o.isValid === true;
  const message = String(o.message ?? "");
  const shareRaw = o.share;
  const workspaceRaw = o.workspace;
  const accessRaw = o.access;

  return {
    isValid,
    message,
    share: shareRaw && typeof shareRaw === "object" ? mapRowToWorkspaceShare(shareRaw as Record<string, unknown>) : null,
    workspace: workspaceRaw && typeof workspaceRaw === "object" ? mapRowToWorkspace(workspaceRaw as Record<string, unknown>) : null,
    access: accessRaw && typeof accessRaw === "object" ? mapRowToWorkspaceShareAccess(accessRaw as Record<string, unknown>) : null,
  };
}

class WorkspaceSharingService {
  private generateShareToken(): string {
    const randomBytes = new Uint8Array(24);
    crypto.getRandomValues(randomBytes);
    const base64 = btoa(String.fromCharCode(...randomBytes));
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").substring(0, 32);
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  /**
   * Create or update a share link. Only the workspace owner (creator or org owner role) can share.
   */
  async createShareLink(params: CreateWorkspaceShareLinkParams): Promise<WorkspaceShare> {
    const userId = await this.getCurrentUserId();

    const ws = await workspacesService.getWorkspace(params.workspaceId);
    if (!ws || ws.role !== "owner") {
      throw new Error("Workspace not found or you don't have permission to share it");
    }

    const { data: existingShare, error: existingErr } = await supabase
      .from("workspace_shares")
      .select(
        "id, workspace_id, created_by_user_id, share_token, access_type, expires_at, is_active, default_member_role, created_at, updated_at"
      )
      .eq("workspace_id", params.workspaceId)
      .eq("is_active", "true")
      .maybeSingle();

    if (existingErr) {
      throw new Error(`Failed to look up share link: ${existingErr.message}`);
    }

    if (existingShare) {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (params.accessType) updateData.access_type = params.accessType;
      if (params.expiresAt !== undefined) updateData.expires_at = params.expiresAt?.toISOString() || null;
      if (params.defaultMemberRole) updateData.default_member_role = params.defaultMemberRole;

      const { data: updatedShare, error: updateError } = await supabase
        .from("workspace_shares")
        .update(updateData)
        .eq("id", existingShare.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update share link: ${updateError.message}`);
      }

      return mapRowToWorkspaceShare(updatedShare as Record<string, unknown>);
    }

    const shareToken = this.generateShareToken();
    const shareData: Record<string, unknown> = {
      workspace_id: params.workspaceId,
      created_by_user_id: userId,
      share_token: shareToken,
      access_type: params.accessType || "restricted",
      expires_at: params.expiresAt?.toISOString() || null,
      is_active: "true",
      default_member_role: params.defaultMemberRole || "viewer",
    };

    const { data: newShare, error: createError } = await supabase
      .from("workspace_shares")
      .insert(shareData)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to create share link: ${createError.message}`);
    }

    return mapRowToWorkspaceShare(newShare as Record<string, unknown>);
  }

  async validateShareLink(token: string): Promise<WorkspaceShareLinkValidationResult> {
    const { data, error } = await supabase.rpc("workspace_share_validate", { p_token: token });
    if (error) {
      return {
        isValid: false,
        share: null,
        workspace: null,
        access: null,
        message: error.message || "Failed to validate link",
      };
    }
    return parseRpcValidationPayload(data);
  }

  async acceptShareInvitation(token: string): Promise<WorkspaceShareAccess> {
    const { data, error } = await supabase.rpc("workspace_share_accept", { p_token: token });
    if (error) {
      throw new Error(error.message || "Failed to accept invitation");
    }
    const parsed = parseRpcValidationPayload(data);
    if (!parsed.access) {
      throw new Error("Failed to accept invitation");
    }
    return parsed.access;
  }

  async rejectShareInvitation(token: string): Promise<WorkspaceShareAccess> {
    const { data, error } = await supabase.rpc("workspace_share_reject", { p_token: token });
    if (error) {
      throw new Error(error.message || "Failed to reject invitation");
    }
    const parsed = parseRpcValidationPayload(data);
    if (!parsed.access) {
      throw new Error("Failed to reject invitation");
    }
    return parsed.access;
  }

  async revokeShareLink(workspaceId: string): Promise<boolean> {
    const ws = await workspacesService.getWorkspace(workspaceId);
    if (!ws || ws.role !== "owner") {
      throw new Error("Only the workspace owner can revoke the share link");
    }

    const { error } = await supabase
      .from("workspace_shares")
      .update({
        is_active: "false",
        updated_at: new Date().toISOString(),
      })
      .eq("workspace_id", workspaceId)
      .eq("is_active", "true");

    if (error) {
      throw new Error(`Failed to revoke share link: ${error.message}`);
    }

    return true;
  }

  async getShareLink(workspaceId: string): Promise<WorkspaceShare | null> {
    const ws = await workspacesService.getWorkspace(workspaceId);
    if (!ws || ws.role !== "owner") {
      return null;
    }

    const { data: share, error } = await supabase
      .from("workspace_shares")
      .select(
        "id, workspace_id, created_by_user_id, share_token, access_type, expires_at, is_active, default_member_role, created_at, updated_at"
      )
      .eq("workspace_id", workspaceId)
      .eq("is_active", "true")
      .maybeSingle();

    if (error || !share) {
      return null;
    }

    return mapRowToWorkspaceShare(share as Record<string, unknown>);
  }

  async hasWorkspaceAccess(workspaceId: string): Promise<boolean> {
    try {
      const ws = await workspacesService.getWorkspace(workspaceId);
      return ws !== null;
    } catch {
      return false;
    }
  }

  async isWorkspaceOwner(workspaceId: string): Promise<boolean> {
    const ws = await workspacesService.getWorkspace(workspaceId);
    return ws?.role === "owner";
  }

  async getWorkspaceOwnerUserId(workspaceId: string): Promise<string | null> {
    const { data: w } = await supabase.from("workspaces").select("created_by_user_id").eq("id", workspaceId).maybeSingle();
    return w?.created_by_user_id ? String(w.created_by_user_id) : null;
  }

  /**
   * Role for sharing/content UI: owner | editor | viewer | null.
   * Native workspace members (admin/member) without a share row are treated as editor so team collaboration keeps working.
   */
  async getCurrentUserWorkspaceShareRole(workspaceId: string): Promise<WorkspaceShareMemberRole | null> {
    const userId = await this.getCurrentUserId();

    const ws = await workspacesService.getWorkspace(workspaceId);
    if (!ws) return null;
    if (ws.role === "owner") return "owner";

    const { data: shares } = await supabase
      .from("workspace_shares")
      .select("id")
      .eq("workspace_id", workspaceId)
      .eq("is_active", "true");

    if (!shares?.length) {
      if (ws.role === "admin" || ws.role === "member") return "editor";
      return null;
    }

    const shareIds = shares.map((s) => s.id);
    const { data: access } = await supabase
      .from("workspace_share_access")
      .select("role")
      .eq("user_id", userId)
      .eq("status", "accepted")
      .in("share_id", shareIds)
      .maybeSingle();

    if (access) {
      return (((access as { role?: string }).role || "viewer") as "editor" | "viewer") || "viewer";
    }

    if (ws.role === "admin" || ws.role === "member") return "editor";

    return null;
  }

  /**
   * Best-effort display names for workspace members (owner-only RPC reads auth.users).
   * Returns {} if the RPC is missing or fails.
   */
  async getWorkspaceMemberDisplayLabels(workspaceId: string): Promise<WorkspaceMemberDisplayLabels> {
    try {
      const { data, error } = await supabase.rpc("workspace_owner_member_display_info", {
        p_workspace_id: workspaceId,
      });
      if (error) {
        console.warn("workspace_owner_member_display_info:", error.message);
        return {};
      }
      let obj: Record<string, unknown> = {};
      const raw = data as unknown;
      if (typeof raw === "string") {
        try {
          obj = JSON.parse(raw) as Record<string, unknown>;
        } catch {
          return {};
        }
      } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        obj = raw as Record<string, unknown>;
      } else {
        return {};
      }
      const out: WorkspaceMemberDisplayLabels = {};
      for (const [k, v] of Object.entries(obj)) {
        if (v && typeof v === "object" && !Array.isArray(v)) {
          const label = (v as { displayLabel?: string }).displayLabel;
          if (typeof label === "string" && label.trim()) {
            out[k] = label.trim();
          }
        }
      }
      return out;
    } catch (e) {
      console.warn("getWorkspaceMemberDisplayLabels:", e);
      return {};
    }
  }

  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMemberEntry[]> {
    try {
      const members: WorkspaceMemberEntry[] = [];

      const { data: wrow } = await supabase
        .from("workspaces")
        .select("created_by_user_id, created_at")
        .eq("id", workspaceId)
        .maybeSingle();

      if (wrow) {
        members.push({
          userId: String(wrow.created_by_user_id),
          workspaceRole: "owner",
          status: "accepted",
          joinedAt: String(wrow.created_at),
        });
      }

      const { data: shares } = await supabase
        .from("workspace_shares")
        .select("id")
        .eq("workspace_id", workspaceId)
        .eq("is_active", "true");

      if (shares && shares.length > 0) {
        const shareIds = shares.map((s) => s.id);
        const { data: accessRecords } = await supabase
          .from("workspace_share_access")
          .select("user_id, responded_at, role")
          .in("share_id", shareIds)
          .eq("status", "accepted");

        if (accessRecords) {
          for (const access of accessRecords) {
            if (String(access.user_id) !== String(wrow?.created_by_user_id)) {
              const sr = ((access.role as string) || "viewer") as "editor" | "viewer";
              members.push({
                userId: String(access.user_id),
                workspaceRole: sr,
                status: "accepted",
                joinedAt: access.responded_at || new Date().toISOString(),
              });
            }
          }
        }
      }

      return members;
    } catch (e) {
      console.error("Error fetching workspace members:", e);
      return [];
    }
  }

  async updateMemberWorkspaceShareRole(
    workspaceId: string,
    memberUserId: string,
    role: "editor" | "viewer"
  ): Promise<void> {
    const { error } = await supabase.rpc("shared_update_workspace_member_role", {
      p_workspace_id: workspaceId,
      p_member_user_id: memberUserId,
      p_role: role,
    });
    if (error) throw new Error(error.message || "Failed to update member role");
  }

  async removeMember(workspaceId: string, memberUserId: string): Promise<void> {
    const isOwner = await this.isWorkspaceOwner(workspaceId);
    if (!isOwner) {
      throw new Error("Only the workspace owner can remove members");
    }

    const creatorId = await this.getWorkspaceOwnerUserId(workspaceId);
    if (creatorId && memberUserId === creatorId) {
      throw new Error("Cannot remove the workspace creator");
    }

    const { error } = await supabase.rpc("shared_remove_workspace_member", {
      p_workspace_id: workspaceId,
      p_member_user_id: memberUserId,
    });
    if (error) {
      throw new Error(error.message || "Failed to remove member");
    }
  }
}

export const workspaceSharingService = new WorkspaceSharingService();
